import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", project_type: "custom_home", customer_name: "", customer_email: "", address: "", status: "planning", progress: 0, start_date: "", end_date: "", budget: 0, description: "", milestones: [] };

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;

const Pick = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const ProjectForm = ({ open, onOpenChange, project, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState(EMPTY);
  const [ms, setMs] = useState("");
  useEffect(() => {
    if (open) setF(project ? { ...EMPTY, ...project } : EMPTY);
  }, [open, project]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, progress: Number(f.progress) || 0, budget: Number(f.budget) || 0 };
    try {
      const saved = project ? await api("put", `/projects/${project.id}`, body) : await api("post", "/projects", body);
      toast.success("Project saved");
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const addMs = () => {
    if (!ms.trim()) return;
    setF({ ...f, milestones: [...f.milestones, { id: crypto.randomUUID(), title: ms.trim(), done: false }] });
    setMs("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">{project ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="project-form">
          <Field label="Project name"><Input required value={f.name} onChange={set("name")} data-testid="project-name-input" /></Field>
          <Field label="Type"><Pick value={f.project_type} onChange={(v) => setF({ ...f, project_type: v })} options={[["custom_home", "Custom home"], ["concrete", "Concrete work"], ["other", "Other"]]} testId="project-type-select" /></Field>
          <Field label="Customer name"><Input value={f.customer_name} onChange={set("customer_name")} data-testid="project-customer-name-input" /></Field>
          <Field label="Customer email (links their portal)"><Input type="email" value={f.customer_email} onChange={set("customer_email")} data-testid="project-customer-email-input" /></Field>
          <Field label="Job address"><Input value={f.address} onChange={set("address")} data-testid="project-address-input" /></Field>
          <Field label="Status"><Pick value={f.status} onChange={(v) => setF({ ...f, status: v })} options={[["planning", "Planning"], ["in_progress", "In progress"], ["on_hold", "On hold"], ["completed", "Completed"]]} testId="project-status-select" /></Field>
          <Field label="Start date"><Input type="date" value={f.start_date} onChange={set("start_date")} data-testid="project-start-input" /></Field>
          <Field label="Target completion"><Input type="date" value={f.end_date} onChange={set("end_date")} data-testid="project-end-input" /></Field>
          <Field label="Progress %"><Input type="number" min="0" max="100" value={f.progress} onChange={set("progress")} data-testid="project-progress-input" /></Field>
          <Field label="Budget ($)"><Input type="number" min="0" step="0.01" value={f.budget} onChange={set("budget")} data-testid="project-budget-input" /></Field>
          <div className="sm:col-span-2"><Field label="Description"><Textarea value={f.description} onChange={set("description")} data-testid="project-description-input" /></Field></div>
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-xs">Milestones</Label>
            {f.milestones.map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-md bg-[#F2EFE9] px-3 py-1.5 text-sm">
                {m.title}
                <button type="button" onClick={() => setF({ ...f, milestones: f.milestones.filter((x) => x.id !== m.id) })} data-testid={`remove-milestone-${m.id}`}><X size={14} /></button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input placeholder="e.g. Foundation poured" value={ms} onChange={(e) => setMs(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMs())} data-testid="milestone-input" />
              <button type="button" onClick={addMs} className="rounded-md border px-3" data-testid="add-milestone-btn"><Plus size={16} /></button>
            </div>
          </div>
          <button className="rounded-full bg-[#F2B91C] py-3 text-sm font-semibold text-[#0E1F3F] hover:bg-[#D9A30F] sm:col-span-2" data-testid="project-save-btn">Save project</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
